import { useMediaQuery, useTheme } from '@material-ui/core';
import React from 'react';

import { useDrawerWidth } from 'src/features/drawer/useDrawerWidth';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { RootState } from 'src/store/reduxTypes';
import { togglePrimarySidebarMobile } from 'src/store/ui/actions';

export interface ShellContextValue {
  drawerWidth: number;
  sidebarOpen: boolean;
  openMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  isMobile: boolean;
  showContextBar: boolean;
  setShowContextBar: (show: boolean) => void;
  contextBarContentMinWidth: number | undefined;
  setContextBarContentMinWidth: (minWidth: number | undefined) => void;
}

export const ShellContext = React.createContext<ShellContextValue | null>(null);

export const ShellProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const drawerWidth = useDrawerWidth();

  const sidebarOpen = useAppSelector(
    (state: RootState) => state.ui.primarySideBarMobile.isOpen,
  );

  const [showContextBar, setShowContextBar] = React.useState(false);
  const [contextBarContentMinWidth, setContextBarContentMinWidth] =
    React.useState<number | undefined>(undefined);

  // Dispatch the desired state directly to avoid stale-closure issues
  // that occur with a toggle pattern (where the callback captures sidebarOpen).
  const openMobileSidebar = React.useCallback(() => {
    dispatch(togglePrimarySidebarMobile({ isOpen: true }));
  }, [dispatch]);

  const closeMobileSidebar = React.useCallback(() => {
    dispatch(togglePrimarySidebarMobile({ isOpen: false }));
  }, [dispatch]);

  const value = React.useMemo<ShellContextValue>(
    () => ({
      drawerWidth,
      sidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      isMobile,
      showContextBar,
      setShowContextBar,
      contextBarContentMinWidth,
      setContextBarContentMinWidth,
    }),
    [
      drawerWidth,
      sidebarOpen,
      openMobileSidebar,
      closeMobileSidebar,
      isMobile,
      showContextBar,
      contextBarContentMinWidth,
    ],
  );

  return (
    <ShellContext.Provider value={value}>{children}</ShellContext.Provider>
  );
};

export function useShell(): ShellContextValue {
  const context = React.useContext(ShellContext);
  if (context === null) {
    throw new Error('useShell must be used within a ShellProvider');
  }
  return context;
}
